import { addDays, format, startOfWeek } from "date-fns";
import { AlertCircle, Calendar, Clock, Coffee } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ShiftTemplate {
  id: string;
  name: string;
  description?: string;
  position?: string;
  scheduleType: string;
  days: number[];
  color?: string;
  timeIntervals: Array<{
    id: string;
    startTime: string;
    endTime: string;
  }>;
  breakRules: Array<{
    id: string;
    afterHours: number;
    breakType: string;
    duration: number;
    isPaid: boolean;
  }>;
}

interface TemplatePreviewDialogProps {
  isOpen: boolean;
  onClose: () => void;
  template: ShiftTemplate | null;
}

const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const BREAK_TYPE_LABELS = {
  REST: "Rest Break",
  BREAK: "Standard Break",
  MEAL: "Meal Break",
  TRAINING: "Training",
  PERSONAL: "Personal Time",
};

export function TemplatePreviewDialog({ isOpen, onClose, template }: TemplatePreviewDialogProps) {
  if (!template) return null;

  const calculateTotalHours = (intervals: ShiftTemplate["timeIntervals"]): number => {
    return intervals.reduce((total, interval) => {
      const start = new Date(`2000-01-01T${interval.startTime}:00`);
      const end = new Date(`2000-01-01T${interval.endTime}:00`);
      const diff = end.getTime() - start.getTime();
      return total + diff / (1000 * 60 * 60);
    }, 0);
  };

  const calculateBreakTime = (breakRules: ShiftTemplate["breakRules"]): number => {
    return breakRules.reduce((total, rule) => total + rule.duration, 0);
  };

  const generateWeekPreview = () => {
    const weekStart = startOfWeek(new Date(), { weekStartsOn: 0 }); // Start on Sunday
    const days: Array<{
      date: Date;
      dayOfWeek: number;
      dayName: string;
      isActiveDay: boolean;
      intervals: ShiftTemplate["timeIntervals"];
      breaks: Array<{
        id: string;
        afterHours: number;
        breakType: string;
        duration: number;
        isPaid: boolean;
        suggestedStartTime: string;
      }>;
    }> = [];

    for (let i = 0; i < 7; i++) {
      const date = addDays(weekStart, i);
      const dayOfWeek = date.getDay();
      const isActiveDay = template.days.includes(dayOfWeek === 0 ? 7 : dayOfWeek); // Convert Sunday (0) to 7 for our 1-7 system

      days.push({
        date,
        dayOfWeek,
        dayName: DAYS_OF_WEEK[dayOfWeek],
        isActiveDay,
        intervals: isActiveDay ? template.timeIntervals : [],
        breaks: isActiveDay ? calculateApplicableBreaks() : [],
      });
    }

    return days;
  };

  const calculateApplicableBreaks = () => {
    const totalHours = calculateTotalHours(template.timeIntervals);
    return template.breakRules
      .filter((rule) => totalHours >= rule.afterHours)
      .map((rule) => ({
        ...rule,
        suggestedStartTime: calculateBreakStartTime(rule.afterHours),
      }));
  };

  const calculateBreakStartTime = (afterHours: number): string => {
    if (template.timeIntervals.length === 0) return "12:00";

    const firstInterval = template.timeIntervals[0];
    const startTime = new Date(`2000-01-01T${firstInterval.startTime}:00`);
    const breakTime = new Date(startTime.getTime() + afterHours * 60 * 60 * 1000);

    return breakTime.toTimeString().slice(0, 5);
  };

  const weekPreview = generateWeekPreview();
  const totalHours = calculateTotalHours(template.timeIntervals);
  const totalBreakTime = calculateBreakTime(template.breakRules);
  const workingHours = totalHours - totalBreakTime / 60;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Template Preview: {template.name}
          </DialogTitle>
          <DialogDescription>
            Preview of how this template will generate shifts in a typical week
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Template Summary */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: template.color || "#3b82f6" }}
                  />
                  <CardTitle className="text-base">{template.name}</CardTitle>
                </div>
                {template.position && <Badge variant="secondary">{template.position}</Badge>}
              </div>
              {template.description && <CardDescription>{template.description}</CardDescription>}
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-muted-foreground">Schedule Type</div>
                  <div className="font-medium capitalize">
                    {template.scheduleType.replace("_", " ")}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">Total Hours</div>
                  <div className="font-medium">{totalHours.toFixed(1)}h</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Break Time</div>
                  <div className="font-medium">{totalBreakTime}min</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Working Time</div>
                  <div className="font-medium">{workingHours.toFixed(1)}h</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Weekly Preview */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Weekly Schedule Preview</CardTitle>
              <CardDescription>
                Sample week showing how shifts would be generated from this template
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
                {weekPreview.map((day) => (
                  <div
                    key={day.dayOfWeek}
                    className={`p-3 rounded-lg border ${
                      day.isActiveDay
                        ? "bg-background border-border"
                        : "bg-muted border-muted-foreground/20"
                    }`}
                  >
                    <div className="text-center mb-2">
                      <div className="font-medium text-sm">{day.dayName}</div>
                      <div className="text-xs text-muted-foreground">
                        {format(day.date, "MMM d")}
                      </div>
                    </div>

                    {day.isActiveDay ? (
                      <div className="space-y-2">
                        {/* Time Intervals */}
                        {day.intervals.map((interval, index) => (
                          <div key={index} className="text-xs space-y-1">
                            <div className="flex items-center gap-1 text-foreground">
                              <Clock className="w-3 h-3" />
                              <span>
                                {interval.startTime}-{interval.endTime}
                              </span>
                            </div>
                          </div>
                        ))}

                        {/* Breaks */}
                        {day.breaks.length > 0 && (
                          <div className="space-y-1 pt-1 border-t border-muted">
                            {day.breaks.map((breakRule, index) => (
                              <div
                                key={index}
                                className="flex items-center gap-1 text-xs text-muted-foreground"
                              >
                                <Coffee className="w-3 h-3" />
                                <span>
                                  {breakRule.duration}min{" "}
                                  {
                                    BREAK_TYPE_LABELS[
                                      breakRule.breakType as keyof typeof BREAK_TYPE_LABELS
                                    ]
                                  }
                                  {breakRule.isPaid && " (Paid)"}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center text-xs text-muted-foreground">No shift</div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Time Intervals Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Time Intervals</CardTitle>
              <CardDescription>
                Normalized time intervals that will be used for shift generation
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {template.timeIntervals.map((interval, index) => (
                  <div
                    key={interval.id}
                    className="flex items-center justify-between p-2 bg-muted rounded"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">Interval {index + 1}</span>
                      <span className="text-sm text-muted-foreground">
                        {interval.startTime} - {interval.endTime}
                      </span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {(() => {
                        const start = new Date(`2000-01-01T${interval.startTime}:00`);
                        const end = new Date(`2000-01-01T${interval.endTime}:00`);
                        const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
                        return `${hours.toFixed(1)}h`;
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Break Rules Details */}
          {template.breakRules.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Break Rules</CardTitle>
                <CardDescription>
                  Automatic break insertion rules based on shift duration
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {template.breakRules.map((rule, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <Coffee className="w-4 h-4 text-muted-foreground" />
                        <div>
                          <div className="font-medium text-sm">
                            {BREAK_TYPE_LABELS[rule.breakType as keyof typeof BREAK_TYPE_LABELS]}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {rule.duration} minutes • After {rule.afterHours} hours
                            {rule.isPaid ? " • Paid" : " • Unpaid"}
                          </div>
                        </div>
                      </div>
                      {totalHours >= rule.afterHours ? (
                        <Badge variant="default" className="text-xs">
                          Applied
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">
                          Not Applied
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>

                {totalHours < Math.min(...template.breakRules.map((r) => r.afterHours)) && (
                  <div className="flex items-center gap-2 p-3 bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded-lg mt-3">
                    <AlertCircle className="w-4 h-4 text-yellow-600" />
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      No breaks will be applied - shift duration ({totalHours.toFixed(1)}h) is less
                      than the minimum required hours.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Layer System Info */}
          <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h4 className="text-blue-900 dark:text-blue-100 font-medium mb-2">
              Four-Layer System Integration
            </h4>
            <div className="text-blue-800 dark:text-blue-200 text-sm space-y-1">
              <p>
                • <strong>Layer 1 (Templates):</strong> This template defines reusable patterns
              </p>
              <p>
                • <strong>Layer 2 (Worker Plans):</strong> Workers can be assigned this template or
                create custom plans
              </p>
              <p>
                • <strong>Layer 3 (Patterns):</strong> System generates optimized shift patterns
                from assignments
              </p>
              <p>
                • <strong>Layer 4 (Shifts):</strong> Concrete shifts are created for daily
                operations and booking
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
